import React, { useState, useEffect } from 'react';
import { Box, IconButton, TextField, Button, styled, Popover } from '@mui/material';
import SentimentSatisfiedAltOutlined from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import SentimentSatisfiedAlt from '@mui/icons-material/SentimentSatisfiedAlt';
import SentimentDissatisfiedOutlined from '@mui/icons-material/SentimentDissatisfiedOutlined';
import SentimentDissatisfied from '@mui/icons-material/SentimentDissatisfied';
import SentimentNeutralOutlined from '@mui/icons-material/SentimentNeutralOutlined';
import SentimentNeutral from '@mui/icons-material/SentimentNeutral';
import { MessageFeedback } from '../types/Thread';

interface FeedbackWidgetProps {
    messageId: string;
    existingFeedback?: MessageFeedback | undefined;
    onFeedbackSubmit: (messageId: string, feedback: MessageFeedback) => void;
}

export const FeedbackWidget: React.FC<FeedbackWidgetProps> = ({
    messageId,
    existingFeedback,
    onFeedbackSubmit,
}) => {
    const [selectedRating, setSelectedRating] = useState<'good' | 'bad' | 'neutral' | null>(
        existingFeedback?.rating || null
    );
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [feedbackText, setFeedbackText] = useState<string>(existingFeedback?.comment || '');
    const [hasSubmitted, setHasSubmitted] = useState<boolean>(!!existingFeedback);

    // Sync state with props when existingFeedback changes (e.g., when switching threads or loading data)
    useEffect(() => {
        setSelectedRating(existingFeedback?.rating || null);
        setFeedbackText(existingFeedback?.comment || '');
        setHasSubmitted(!!existingFeedback);
    }, [existingFeedback?.rating, existingFeedback?.comment, existingFeedback]);

    const handleRatingClick = (rating: 'good' | 'bad' | 'neutral', event: React.MouseEvent<HTMLElement>) => {
        const newRating = selectedRating === rating ? null : rating;
        setSelectedRating(newRating);
        
        if (newRating) {
            // Show popover for optional comment
            setAnchorEl(event.currentTarget);
            
            // If selecting a different rating after submission, clear the text
            if (hasSubmitted && newRating !== existingFeedback?.rating) {
                setFeedbackText('');
                setHasSubmitted(false);
            }
            
            // Immediately submit the rating (without comment) to persist it
            const feedback: MessageFeedback = {
                rating: newRating,
                timestamp: Date.now(),
            };
            onFeedbackSubmit(messageId, feedback);
        } else {
            // Close popover and reset
            setAnchorEl(null);
            setFeedbackText('');
            setHasSubmitted(false);
        }
    };

    const handleClosePopover = () => {
        setAnchorEl(null);
    };

    const handleSubmitFeedback = () => {
        if (selectedRating) {
            const trimmedComment = feedbackText.trim();
            const feedback: MessageFeedback = {
                rating: selectedRating,
                ...(trimmedComment && { comment: trimmedComment }),
                timestamp: Date.now(),
            };
            onFeedbackSubmit(messageId, feedback);
            
            // Close popover after submission
            setAnchorEl(null);
            setHasSubmitted(true);
        }
    };

    const open = Boolean(anchorEl);

    return (
        <FeedbackContainer>
            <IconsRow>
                <StyledIconButton
                    onClick={(e) => handleRatingClick('good', e)}
                    aria-label="Good response"
                    size="small"
                >
                    {selectedRating === 'good' ? (
                        <SentimentSatisfiedAlt sx={{ fontSize: 22, color: '#0FCB8C' }} />
                    ) : (
                        <SentimentSatisfiedAltOutlined sx={{ fontSize: 22, color: '#FFFFFF80' }} />
                    )}
                </StyledIconButton>

                <StyledIconButton
                    onClick={(e) => handleRatingClick('neutral', e)}
                    aria-label="Neutral response"
                    size="small"
                >
                    {selectedRating === 'neutral' ? (
                        <SentimentNeutral sx={{ fontSize: 22, color: '#FFA500' }} />
                    ) : (
                        <SentimentNeutralOutlined sx={{ fontSize: 22, color: '#FFFFFF80' }} />
                    )}
                </StyledIconButton>

                <StyledIconButton
                    onClick={(e) => handleRatingClick('bad', e)}
                    aria-label="Bad response"
                    size="small"
                >
                    {selectedRating === 'bad' ? (
                        <SentimentDissatisfied sx={{ fontSize: 22, color: '#FF6B6B' }} />
                    ) : (
                        <SentimentDissatisfiedOutlined sx={{ fontSize: 22, color: '#FFFFFF80' }} />
                    )}
                </StyledIconButton>
            </IconsRow>

            {/* Popover for optional feedback comment */}
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClosePopover}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                disableScrollLock
                sx={{
                    '& .MuiPopover-paper': {
                        bgcolor: '#0A3235',
                        border: '1px solid #0FCB8C55',
                        p: 2,
                        mt: 0.5,
                    }
                }}
            >
                <PopoverContent>
                    <StyledTextField
                        size="small"
                        placeholder="Add optional feedback (if referencing a figure, include the figure name)..."
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        fullWidth
                        multiline
                        rows={2}
                        autoFocus
                        sx={{
                            bgcolor: '#032629',
                            width: 300,
                            '& .MuiInputBase-input': { 
                                color: '#FFFFFF',
                                fontSize: '0.875rem',
                            },
                            '& .MuiOutlinedInput-notchedOutline': { 
                                borderColor: '#0FCB8C55' 
                            },
                            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#0FCB8C88'
                            },
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#0FCB8C'
                            }
                        }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
                        <Button 
                            variant="outlined" 
                            size="small" 
                            onClick={handleClosePopover}
                            sx={{
                                color: '#FFFFFF80',
                                borderColor: '#FFFFFF40',
                                '&:hover': {
                                    borderColor: '#FFFFFF80',
                                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                                }
                            }}
                        >
                            Skip
                        </Button>
                        <SubmitButton 
                            variant="contained" 
                            size="small" 
                            onClick={handleSubmitFeedback}
                        >
                            Submit
                        </SubmitButton>
                    </Box>
                </PopoverContent>
            </Popover>
        </FeedbackContainer>
    );
};

const FeedbackContainer = styled(Box)`
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const PopoverContent = styled(Box)`
    display: flex;
    flex-direction: column;
`;

const IconsRow = styled(Box)`
    display: flex;
    gap: 4px;
    align-items: center;
`;

const StyledIconButton = styled(IconButton)`
    padding: 6px;
    &:hover {
        background-color: rgba(15, 203, 140, 0.08);
    }
`;

const StyledTextField = styled(TextField)`
    flex: 1;
`;

const SubmitButton = styled(Button)`
    background-color: #0FCB8C;
    color: #032629;
    font-weight: 600;
    text-transform: none;
    padding: 4px 16px;
    min-width: 70px;
    
    &:hover {
        background-color: #0A9B72;
    }
    
    &:disabled {
        background-color: #0FCB8C40;
        color: #03262980;
    }
`;
